import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, Mail, CheckCircle, MapPin, Clock, Shield, ThumbsUp, Award } from 'lucide-react';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    ownershipLength: '',
    serviceType: '',
    interests: [],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showOutOfAreaWarning, setShowOutOfAreaWarning] = useState(false);
  const [locationCheckTimeout, setLocationCheckTimeout] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    if (e.target.name === 'location') {
      if (locationCheckTimeout) {
        clearTimeout(locationCheckTimeout);
      }
      
      const locationValue = e.target.value;
      
      if (locationValue.trim().length < 5) {
        setShowOutOfAreaWarning(false);
        return;
      }
      
      const timeout = setTimeout(() => {
        checkServiceArea(locationValue);
      }, 1500);
      
      setLocationCheckTimeout(timeout);
    }
  };
  
  const checkServiceArea = (location) => {
    if (!location.trim()) {
      setShowOutOfAreaWarning(false);
      return;
    }
    
    const locationLower = location.toLowerCase();
    
    const servedMunicipalities = {
      bergen: ['allendale', 'alpine', 'bergenfield', 'bogota', 'carlstadt', 'cliffside park', 'closter', 'cresskill', 'demarest', 'dumont', 'east rutherford', 'edgewater', 'elmwood park', 'emerson', 'englewood', 'englewood cliffs', 'fair lawn', 'fairview', 'fort lee', 'franklin lakes', 'garfield', 'glen rock', 'hackensack', 'harrington park', 'hasbrouck heights', 'haworth', 'hillsdale', 'ho-ho-kus', 'leonia', 'little ferry', 'lodi', 'lyndhurst', 'mahwah', 'maywood', 'midland park', 'montvale', 'moonachie', 'new milford', 'north arlington', 'northvale', 'norwood', 'oakland', 'old tappan', 'oradell', 'palisades park', 'paramus', 'park ridge', 'ramsey', 'ridgefield', 'ridgefield park', 'ridgewood', 'river edge', 'river vale', 'rochelle park', 'rockleigh', 'rutherford', 'saddle brook', 'saddle river', 'south hackensack', 'teaneck', 'tenafly', 'teterboro', 'upper saddle river', 'waldwick', 'wallington', 'washington township', 'westwood', 'woodcliff lake', 'wood-ridge', 'wyckoff'],
      hudson: ['bayonne', 'east newark', 'guttenberg', 'harrison', 'hoboken', 'jersey city', 'kearny', 'north bergen', 'secaucus', 'union city', 'weehawken', 'west new york'],
      essex: ['belleville', 'bloomfield', 'caldwell', 'cedar grove', 'east orange', 'essex fells', 'fairfield', 'glen ridge', 'irvington', 'livingston', 'maplewood', 'millburn', 'montclair', 'newark', 'north caldwell', 'nutley', 'orange', 'roseland', 'south orange', 'verona', 'west caldwell', 'west orange'],
      passaic: ['bloomingdale', 'clifton', 'haledon', 'hawthorne', 'little falls', 'north haledon', 'passaic', 'paterson', 'pompton lakes', 'prospect park', 'ringwood', 'totowa', 'wanaque', 'wayne', 'west milford', 'woodland park'],
      morris: ['boonton', 'boonton township', 'butler', 'chatham', 'chatham township', 'chester', 'chester township', 'denville', 'dover', 'east hanover', 'florham park', 'hanover', 'harding', 'jefferson', 'kinnelon', 'lincoln park', 'madison', 'mendham', 'mendham township', 'mine hill', 'montville', 'morris plains', 'morris township', 'morristown', 'mount arlington', 'mount olive', 'mountain lakes', 'netcong', 'parsippany-troy hills', 'parsippany', 'pequannock', 'randolph', 'riverdale', 'rockaway', 'rockaway township', 'roxbury', 'victory gardens', 'washington township', 'wharton'],
      middlesex: ['carteret', 'cranbury', 'dunellen', 'east brunswick', 'edison', 'helmetta', 'highland park', 'jamesburg', 'metuchen', 'middlesex', 'milltown', 'monroe', 'new brunswick', 'north brunswick', 'old bridge', 'perth amboy', 'piscataway', 'plainsboro', 'sayreville', 'south amboy', 'south brunswick', 'south plainfield', 'south river', 'spotswood', 'woodbridge'],
      sussex: ['andover', 'andover township', 'branchville', 'byram', 'byram township', 'frankford', 'frankford township', 'franklin', 'fredon', 'fredon township', 'green', 'green township', 'hamburg', 'hampton', 'hampton township', 'hardyston', 'hardyston township', 'hopatcong', 'lafayette', 'lafayette township', 'montague', 'montague township', 'newton', 'ogdensburg', 'sandyston', 'sandyston township', 'sparta', 'sparta township', 'stanhope', 'stillwater', 'stillwater township', 'sussex', 'vernon', 'vernon township', 'walpack', 'walpack township', 'wantage', 'wantage township'],
      somerset: ['bedminster', 'bernards', 'bernards township', 'bernardsville', 'bound brook', 'branchburg', 'branchburg township', 'bridgewater', 'bridgewater township', 'far hills', 'franklin', 'franklin township', 'green brook', 'green brook township', 'hillsborough', 'hillsborough township', 'manville', 'millstone', 'montgomery', 'montgomery township', 'north plainfield', 'peapack-gladstone', 'peapack', 'gladstone', 'raritan', 'rocky hill', 'somerset', 'somerville', 'south bound brook', 'warren', 'warren township', 'watchung'],
      union: ['berkeley heights', 'clark', 'cranford', 'elizabeth', 'fanwood', 'garwood', 'hillside', 'kenilworth', 'linden', 'mountainside', 'new providence', 'plainfield', 'rahway', 'roselle', 'roselle park', 'scotch plains', 'springfield', 'summit', 'union', 'union township', 'westfield', 'winfield', 'winfield township']
    };
    
    const servedCounties = ['bergen', 'hudson', 'essex', 'passaic', 'morris', 'middlesex', 'sussex', 'somerset', 'union'];
    const hasCountyName = servedCounties.some(county => locationLower.includes(county));
    
    if (hasCountyName) {
      setShowOutOfAreaWarning(false);
      return;
    }
    
    const allMunicipalities = Object.values(servedMunicipalities).flat();
    const hasCityName = allMunicipalities.some(city => locationLower.includes(city));
    setShowOutOfAreaWarning(!hasCityName);
  };

  const handleCheckboxChange = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const servedCounties = ['bergen', 'hudson', 'essex', 'passaic', 'morris', 'middlesex', 'sussex', 'somerset', 'union'];
    const locationLower = formData.location.toLowerCase();
    const isInServiceArea = servedCounties.some(county => locationLower.includes(county));
    const hasNJ = locationLower.includes('nj') || locationLower.includes('new jersey');
    const shouldWarnOutOfArea = !isInServiceArea && hasNJ;

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      if (isInServiceArea) {
        toast.success('Message sent successfully!', {
          description: 'We\'ll get back to you soon.',
        });
      } else if (shouldWarnOutOfArea || !hasNJ) {
        toast.success('Message received!', {
          description: 'We noticed you\'re outside our current service area. We\'ll contact you if we expand to your location.',
          duration: 6000,
        });
      } else {
        toast.success('Message sent successfully!', {
          description: 'We\'ll get back to you soon.',
        });
      }

      setFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        ownershipLength: '',
        serviceType: '',
        interests: [],
        message: ''
      });

      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setIsSubmitting(false);
      
      toast.error('Failed to send message', {
        description: 'Please try again or contact us directly.',
      });
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <Toaster position="top-center" richColors />
      
      {/* Hero Section - Premium Style */}
      <section className="relative py-20 md:py-24 bg-[#003366] text-white overflow-hidden">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        {/* Logo Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/0d36dd51_Transparent%20Png-02.png" alt="" className="w-[400px] md:w-[500px] h-auto opacity-[0.06]" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="mb-6">
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-2">
              Let's Start the
            </span>
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              <span className="text-[#CFB973]">Conversation.</span>
            </span>
          </h1>
          
          {/* Decorative line */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
            <div className="w-2 h-2 bg-[#CFB973] rotate-45"></div>
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
          </div>
          
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            Ready to experience calm, reliable home care? <span className="text-[#CFB973] font-semibold">We're here to help.</span>
          </p>
          
          {/* Quick Contact Options */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:5513799774" 
              className="inline-flex items-center justify-center bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-10 py-4 text-lg shadow-xl rounded-md"
            >
              <Phone className="mr-2" size={20} />
              (551) 379-9774
            </a>
            <a 
              href="mailto:contact@valenrockhome.services" 
              className="inline-flex items-center justify-center bg-transparent hover:bg-white/10 text-white font-semibold px-10 py-4 text-lg border-2 border-[#CFB973] rounded-md"
            >
              <Mail className="mr-2" size={20} />
              Email Us
            </a>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-[#E8EEF3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Shield className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">Licensed & Insured</p>
              <p className="text-[#003366]/60 text-xs">#13VH13590600</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Clock className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">Quick Response</p>
              <p className="text-[#003366]/60 text-xs">Time</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <ThumbsUp className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">No Pressure</p>
              <p className="text-[#003366]/60 text-xs">No Sales Pitch</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-4 border-[#CFB973] shadow-2xl rounded-2xl overflow-hidden">
                <CardHeader className="bg-[#003366] text-white p-8">
                  <CardTitle className="text-2xl font-bold">
                    Request a Consultation
                  </CardTitle>
                  <p className="text-white/80 mt-2">
                    Tell us about your property, how long you've owned it, and what kind of support you're looking for.
                  </p>
                </CardHeader>
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Service Type Selection */}
                    <div>
                      <Label htmlFor="serviceType" className="text-[#003366] font-bold text-base">What are you interested in? *</Label>
                      <select
                        id="serviceType"
                        name="serviceType"
                        value={formData.serviceType}
                        onChange={handleChange}
                        required
                        className="mt-2 w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#CFB973] focus:ring-[#CFB973] focus:ring-2 transition-all"
                      >
                        <option value="">Select a service...</option>
                        <option value="assessment">Whole-Home Assessment</option>
                        <option value="new-homebuyer">New Homebuyer Assessment</option>
                        <option value="investment-property">Investment Property / Landlord Services</option>
                        <option value="realtor-referral">Realtor Partnership Inquiry</option>
                        <option value="seasonal-care">Seasonal Care Package</option>
                        <option value="remodeling">Remodeling Project</option>
                        <option value="maintenance">Ongoing Maintenance</option>
                        <option value="single-room">Single-Room Estimate (Free)</option>
                        <option value="other">Other / General Inquiry</option>
                      </select>
                      {formData.serviceType && (
                        <div className={`mt-3 p-3 rounded-lg ${
                          (formData.serviceType === 'remodeling' || formData.serviceType === 'single-room' || formData.serviceType === 'other')
                            ? 'bg-[#E8EEF3]' 
                            : 'bg-amber-50 border-2 border-amber-300'
                        }`}>
                          <p className={`text-sm ${
                            (formData.serviceType === 'remodeling' || formData.serviceType === 'single-room' || formData.serviceType === 'other')
                              ? 'text-[#003366]' 
                              : 'text-amber-800'
                          }`}>
                            {formData.serviceType === 'single-room' && "✓ Available Now: Looking for something smaller? We're happy to start with a single room."}
                            {formData.serviceType === 'seasonal-care' && "⏳ Waitlist: Join our waitlist — scheduling opens soon."}
                            {formData.serviceType === 'investment-property' && "⏳ Waitlist: Join our waitlist — scheduling opens soon."}
                            {formData.serviceType === 'new-homebuyer' && "⏳ Waitlist: Join our waitlist — scheduling opens soon."}
                            {formData.serviceType === 'realtor-referral' && "⏳ Waitlist: Join our waitlist — scheduling opens soon."}
                            {formData.serviceType === 'assessment' && "⏳ Waitlist: Join our waitlist — scheduling opens soon."}
                            {formData.serviceType === 'remodeling' && "✓ Available Now: Kitchens, bathrooms, basements, and full home renovations."}
                            {formData.serviceType === 'maintenance' && "⏳ Waitlist: Join our waitlist — scheduling opens soon."}
                            {formData.serviceType === 'other' && "✓ Available: Tell us what you need and we'll get back to you."}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-[#003366] font-bold">Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="mt-2 border-2 border-gray-200 rounded-xl focus:border-[#CFB973] focus:ring-[#CFB973] py-3"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-[#003366] font-bold">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-2 border-2 border-gray-200 rounded-xl focus:border-[#CFB973] focus:ring-[#CFB973] py-3"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="text-[#003366] font-bold">Phone *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="mt-2 border-2 border-gray-200 rounded-xl focus:border-[#CFB973] focus:ring-[#CFB973] py-3"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div>
                        <Label htmlFor="location" className="text-[#003366] font-bold">Location *</Label>
                        <Input
                          id="location"
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          required
                          className="mt-2 border-2 border-gray-200 rounded-xl focus:border-[#CFB973] focus:ring-[#CFB973] py-3"
                          placeholder="City, County, State"
                        />
                        {showOutOfAreaWarning && (
                          <div className="mt-2 p-3 bg-amber-50 border-2 border-amber-300 rounded-xl">
                            <p className="text-sm text-amber-800">
                              <strong>Outside Service Area:</strong> We currently serve Northern & Central NJ. You can still submit, and we'll contact you if we expand.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="ownershipLength" className="text-[#003366] font-bold">How long have you owned your property?</Label>
                      <Input
                        id="ownershipLength"
                        name="ownershipLength"
                        value={formData.ownershipLength}
                        onChange={handleChange}
                        className="mt-2 border-2 border-gray-200 rounded-xl focus:border-[#CFB973] focus:ring-[#CFB973] py-3"
                        placeholder="e.g., 5 years, Just purchased, etc."
                      />
                    </div>

                    <div>
                      <Label className="text-[#003366] font-bold mb-4 block">When are you looking to get started?</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { id: 'within-a-week', label: 'Within a week' },
                          { id: 'within-30-days', label: 'Within 30 days' },
                          { id: 'few-months', label: 'Next few months' },
                          { id: 'exploring', label: 'Just exploring' }
                        ].map(option => (
                          <label 
                            key={option.id}
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              formData.interests.includes(option.id) 
                                ? 'border-[#CFB973] bg-[#CFB973]/10' 
                                : 'border-gray-200 hover:border-[#CFB973]/50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.interests.includes(option.id)}
                              onChange={() => handleCheckboxChange(option.id)}
                              className="w-5 h-5 text-[#CFB973] border-gray-300 rounded focus:ring-[#CFB973]"
                            />
                            <span className="text-gray-700 font-medium">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#003366] font-bold">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="mt-2 border-2 border-gray-200 rounded-xl focus:border-[#CFB973] focus:ring-[#CFB973]"
                        placeholder="Tell us about your home, what you're looking for, and any specific concerns..."
                      />
                    </div>

                    {/* Privacy Consent */}
                    <div className="bg-[#E8EEF3] p-4 rounded-xl">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          required
                          className="w-5 h-5 mt-0.5 text-[#CFB973] border-gray-300 rounded focus:ring-[#CFB973]"
                        />
                        <span className="text-sm text-gray-600">
                          I agree to the{' '}
                          <a href="/privacy-policy" target="_blank" className="text-[#003366] font-semibold hover:text-[#CFB973] underline">
                            Privacy Policy
                          </a>
                          {' '}and{' '}
                          <a href="/terms-of-service" target="_blank" className="text-[#003366] font-semibold hover:text-[#CFB973] underline">
                            Terms of Service
                          </a>. *
                        </span>
                      </label>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#003366] hover:bg-[#003366]/90 text-white py-7 text-lg font-bold rounded-xl shadow-lg"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>

                    {isSubmitted && (
                      <div className="flex items-center justify-center gap-3 text-green-600 bg-green-50 p-4 rounded-xl border-2 border-green-200">
                        <CheckCircle size={24} />
                        <span className="font-bold">Message sent! We'll be in touch soon.</span>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <Card className="border-2 border-gray-100 rounded-2xl overflow-hidden shadow-lg">
                <CardHeader className="bg-[#003366] text-white p-6">
                  <CardTitle className="text-xl font-bold">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Phone className="text-[#003366]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#003366] mb-1">Phone</h3>
                      <a href="tel:5513799774" className="text-gray-600 hover:text-[#CFB973] transition-colors text-lg">
                        (551) 379-9774
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Mail className="text-[#003366]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#003366] mb-1">Email</h3>
                      <a href="mailto:contact@valenrockhome.services" className="text-gray-600 hover:text-[#CFB973] transition-colors break-all">
                        contact@valenrockhome.services
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0 shadow-lg">
                      <MapPin className="text-[#003366]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#003366] mb-1">Service Area</h3>
                      <p className="text-gray-600">Northern & Central New Jersey</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* What Happens Next Card */}
              <Card className="border-4 border-[#CFB973] bg-gradient-to-br from-[#003366] to-[#003366]/95 rounded-2xl overflow-hidden shadow-lg">
                <CardContent className="p-6">
                  <h3 className="font-bold text-white text-xl mb-4">What Happens Next?</h3>
                  <ul className="space-y-4">
                    {[
                      'We\'ll review your message and reach out',
                      'Schedule a no-pressure conversation',
                      'Plan your home walkthrough',
                      'Receive your personalized Home Profile'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="w-4 h-4 text-[#003366]" />
                        </div>
                        <span className="text-white/90">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Service Area Note */}
              <div className="bg-[#E8EEF3] rounded-2xl p-6 border-2 border-[#CFB973]/30">
                <h3 className="font-bold text-[#003366] mb-2">Currently Serving:</h3>
                <p className="text-gray-600 text-sm">
                  Bergen, Essex, Hudson, Passaic, Morris, Middlesex, Sussex, Somerset, and Union Counties
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-6">
            No Pressure. No Sales Pitch.
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto mb-8">
            We're not here to sell you services you don't need. Our first conversation is about understanding your home, your goals, and whether we're the right fit to help you.
          </p>
          <p className="text-xl text-[#003366] font-medium italic">
            "If we're not the right fit, we'll tell you honestly."
          </p>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
